import { AUTH_JOURNEY_MANIFEST, type AuthJourneyId } from './journey-manifest';

export const AUTH_JOURNEY_PRESENTATION_TTL_MS = 30 * 60 * 1000;
export const AUTH_JOURNEY_PRESENTATION_STORAGE_KEY =
  'streamlain:auth-journey:presentation:v1';

export interface AuthJourneyPresentationMarker {
  version: 1;
  journeyId: AuthJourneyId;
  stageId: string;
  startedAt: number;
}

type PresentationStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function isValidMarker(
  value: unknown,
  now: number
): value is AuthJourneyPresentationMarker {
  if (!value || typeof value !== 'object') return false;
  if (
    Object.keys(value).sort().join(',') !==
    'journeyId,stageId,startedAt,version'
  ) {
    return false;
  }
  const marker = value as Partial<AuthJourneyPresentationMarker>;
  if (
    marker.version !== 1 ||
    typeof marker.journeyId !== 'string' ||
    typeof marker.stageId !== 'string' ||
    typeof marker.startedAt !== 'number' ||
    now - marker.startedAt < 0 ||
    now - marker.startedAt > AUTH_JOURNEY_PRESENTATION_TTL_MS
  ) {
    return false;
  }
  const definition = AUTH_JOURNEY_MANIFEST[marker.journeyId as AuthJourneyId];
  return Boolean(
    definition && definition.stages.some(stage => stage.id === marker.stageId)
  );
}

export function saveAuthJourneyPresentationMarker(
  marker: AuthJourneyPresentationMarker,
  storage: PresentationStorage = window.sessionStorage
) {
  if (!isValidMarker(marker, marker.startedAt)) {
    throw new Error('Invalid auth journey presentation marker');
  }
  storage.setItem(
    AUTH_JOURNEY_PRESENTATION_STORAGE_KEY,
    JSON.stringify(marker)
  );
}

export function readAuthJourneyPresentationMarker(
  storage: PresentationStorage = window.sessionStorage,
  now = Date.now()
): AuthJourneyPresentationMarker | null {
  try {
    const raw = storage.getItem(AUTH_JOURNEY_PRESENTATION_STORAGE_KEY);
    if (!raw) return null;
    const marker: unknown = JSON.parse(raw);
    if (!isValidMarker(marker, now)) {
      storage.removeItem(AUTH_JOURNEY_PRESENTATION_STORAGE_KEY);
      return null;
    }
    return {
      version: marker.version,
      journeyId: marker.journeyId,
      stageId: marker.stageId,
      startedAt: marker.startedAt,
    };
  } catch {
    storage.removeItem(AUTH_JOURNEY_PRESENTATION_STORAGE_KEY);
    return null;
  }
}

export function consumeAuthJourneyPresentationMarker(
  storage: PresentationStorage = window.sessionStorage,
  now = Date.now()
): AuthJourneyPresentationMarker | null {
  const marker = readAuthJourneyPresentationMarker(storage, now);
  storage.removeItem(AUTH_JOURNEY_PRESENTATION_STORAGE_KEY);
  return marker;
}
